import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Dot, Icon } from "../../common/components/Icon";
import { Button, Checkbox } from "../../common/components";
import { useTranslation } from "react-i18next";
import { TechpackItem as TechpackItemType } from "../../types";
import { Box, Divider, Menu, MenuItem } from "@mui/material";

import { Link } from "../../common/components/Link";
import { palette } from "../../styles/theme";
import TechpackDownloadService from "../../service/TechpackDownloadService";
import { useTechpackFetcher } from "../../utils/useTechpackFetcher";
import fileDownload from "js-file-download";
import { useAppLoading } from "../../context/AppLoading/useAppLoading";
import { AdminTechpackContext } from "../../context/Admin/Techpack";

const filterSelectedDownloadItems = (item: TechpackItemType) => {
  return item.techpackitemartworks.every(
    (artwork) => artwork.productionDecoration != null
  );
};

const DownloadTechpackButton = () => {
  const { t } = useTranslation();
  const { teckpackDetails } = useContext(AdminTechpackContext);

  const techpackId = teckpackDetails?.id || "";
  const projectNumber = teckpackDetails?.projectNumber || "";
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const { setIsAppLoading } = useAppLoading();
  const ref = useRef<HTMLButtonElement>(null);
  const [selectedDownloadItems, setSelectedDownloadItems] = useState<string[]>(
    teckpackDetails?.techpackitems
      .filter(filterSelectedDownloadItems)
      .map((item) => item.id) || []
  );
  const { fetchTechpackPdfLink } = useTechpackFetcher();
  const handleToggleItem = (itemId: string) => {
    setSelectedDownloadItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const techpackItems = useMemo(
    () => teckpackDetails?.techpackitems || [],
    [teckpackDetails]
  );

  const handleSelectAll = () => {
    setSelectedDownloadItems(
      techpackItems.filter(filterSelectedDownloadItems).map((item) => item.id)
    );
  };

  const handleClear = () => {
    setSelectedDownloadItems([]);
  };

  const handleDownload = () => {
    setIsAppLoading(true);
    const techpackFilename =
      "techpack_" + (projectNumber || techpackId) + ".zip";
    fetchTechpackPdfLink(techpackId, selectedDownloadItems)
      .then((resPdf) => {
        const signedUrl = resPdf.signedUrl;
        TechpackDownloadService.downloadTechpack(signedUrl)
          .then((res) => {
            fileDownload(res.data, techpackFilename);
          })
          .catch(() => {
            console.error("Unable to download the techpack pdf from the link");
          });
      })
      .finally(() => {
        setIsAppLoading(false);
      });
  };

  useEffect(() => {
    setSelectedDownloadItems((prev) =>
      prev.filter((id) =>
        techpackItems
          .filter(filterSelectedDownloadItems)
          .some((item) => item.id === id)
      )
    );
  }, [techpackItems]);

  return (
    <>
      <Button
        ref={ref}
        variant="contained"
        size="small"
        color="primary"
        endIcon={<Icon icon="keyboard_arrow_down" size={18} color="white" />}
        onClick={() => {
          handleSelectAll();
          setIsDownloadOpen(true);
        }}
      >
        {t("techpack.download_techpack")}
      </Button>

      <Menu
        open={isDownloadOpen}
        id="download-techpack-menu"
        anchorEl={ref.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={() => setIsDownloadOpen(false)}
        sx={{
          "& .MuiPaper-root": {
            width: 280,
            mt: "6.5px",
            minWidth: "fit-content",
            py: "8px",
            boxShadow: "1px 1px 10px -2px rgba(17, 25, 39, 0.20)",
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          pb={1}
        >
          <Link
            onClick={(e) => {
              e.preventDefault();
              handleSelectAll();
            }}
            style={{ fontSize: "14px", color: palette.blue.secondary }}
          >
            {t("general.select_all")}
          </Link>
          <Link
            onClick={(e) => {
              e.preventDefault();
              handleClear();
            }}
            style={{ fontSize: "14px", color: palette.blue.secondary }}
          >
            {t("general.clear")}
          </Link>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {techpackItems
          .sort((a, b) => {
            // First sort by factory
            if (a.factory !== b.factory) {
              return a.factory.localeCompare(b.factory);
            }
            // Then sort by factory position
            return a.factoryPosition - b.factoryPosition;
          })
          .map((item, index) => {
            const hasProductionArtwork = item.techpackitemartworks.every(
              (artwork) => artwork.productionDecoration != null
            );

            const isDisabled = !hasProductionArtwork;

            return (
              <MenuItem
                key={item.id}
                onClick={(e) => {
                  e.preventDefault();
                  if (isDisabled) return;
                  handleToggleItem(item.id);
                }}
                sx={{
                  padding: "4px 16px",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                }}
              >
                <Checkbox
                  checked={
                    hasProductionArtwork &&
                    selectedDownloadItems.includes(item.id)
                  }
                  containerSx={
                    isDisabled
                      ? {
                          cursor: "not-allowed !important",
                        }
                      : {}
                  }
                  size="small"
                  disabled={isDisabled}
                  label={`${item.purchaseddesign.originalDesignName} (${item.factoryInfo})`}
                  labelVariant="actionSmallIcon"
                />
                {isDisabled && <Dot color={"error"} />}
              </MenuItem>
            );
          })}

        <Box px={1.5} mt={1.5}>
          <Button
            size="small"
            fullWidth
            onClick={handleDownload}
            disabled={selectedDownloadItems.length === 0}
          >
            {t("general.download")}
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default DownloadTechpackButton;
